/**
 * @file lib/git/chat-git-manager.ts
 * @description チャット履歴のGit管理マネージャー
 * @author GFTD Co., Ltd.
 */

import { GitFileSystem } from './git-fs';
import { ActorSession, ActorMessage } from '../types/session';
import {
  ChatGitConfig,
  ChatHistoryFile,
  ChatHistoryMetadata,
  ChatGitSaveResult,
  ChatHistorySearchQuery,
  ChatHistorySearchResult,
  ChatHistoryFileNaming,
  MarkdownExportOptions,
  IChatGitManager
} from '../types/chat-git';

/**
 * チャット履歴のGit管理クラス
 * セッション管理とGit操作を統合し、チャット履歴をファイルとして永続化
 */
export class ChatGitManager implements IChatGitManager {
  private gitFs: GitFileSystem;
  private config: ChatGitConfig;
  private fileNaming: ChatHistoryFileNaming;
  private markdownOptions: MarkdownExportOptions;
  private isInitialized = false;

  constructor(
    config: ChatGitConfig,
    fileNaming?: Partial<ChatHistoryFileNaming>,
    markdownOptions?: Partial<MarkdownExportOptions>
  ) {
    this.config = config;
    this.gitFs = new GitFileSystem(config.git);

    // ファイル命名規則のデフォルト設定
    this.fileNaming = {
      sessionDirPattern: 'sessions/{actorId}/{sessionId}',
      markdownFilePattern: 'chat-history.md',
      jsonFilePattern: 'chat-history.json',
      metadataFilePattern: 'metadata.json',
      ...fileNaming
    };

    // Markdownエクスポート設定のデフォルト
    this.markdownOptions = {
      titleFormat: '# Chat History - Session {sessionId}',
      messageFormat: '## {role} ({timestamp})\n\n{content}\n',
      timestampFormat: 'YYYY-MM-DD HH:mm:ss',
      includeToc: true,
      includeMetadata: true,
      ...markdownOptions
    };
  }

  /**
   * 初期化
   */
  private async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.gitFs.initialize();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize ChatGitManager:', error);
      throw error;
    }
  }

  /**
   * チャット履歴を保存
   */
  async saveHistory(
    session: ActorSession,
    messages: ActorMessage[],
    commitMessage?: string
  ): Promise<ChatGitSaveResult> {
    try {
      await this.initialize();

      const metadata: ChatHistoryMetadata = {
        sessionId: session.id,
        actorId: session.actor_id,
        createdAt: session.created_at,
        updatedAt: session.updated_at,
        messageCount: messages.length,
        format: this.config.format,
        branch: await this.gitFs.getCurrentBranch()
      };

      const historyFile: ChatHistoryFile = {
        metadata,
        session,
        messages,
        additionalInfo: {
          exportedAt: new Date().toISOString(),
          exportVersion: '1.0.0'
        }
      };

      const sessionDir = this.getSessionDirectory(session.actor_id, session.id);
      const filePaths: string[] = [];

      // ディレクトリを作成（parentディレクトリを経由して作成）
      try {
        await this.gitFs.writeFile(`${sessionDir}/.gitkeep`, '');
      } catch (error) {
        // ディレクトリが存在しない場合の処理
        console.warn('Failed to create directory:', error);
      }

      // JSON形式で保存
      if (this.config.format === 'json' || this.config.format === 'both') {
        const jsonPath = `${sessionDir}/${this.fileNaming.jsonFilePattern}`;
        await this.gitFs.writeFile(jsonPath, JSON.stringify(historyFile, null, 2));
        filePaths.push(jsonPath);
      }

      // Markdown形式で保存
      if (this.config.format === 'markdown' || this.config.format === 'both') {
        const markdownPath = `${sessionDir}/${this.fileNaming.markdownFilePattern}`;
        const markdownContent = this.convertToMarkdown(historyFile);
        await this.gitFs.writeFile(markdownPath, markdownContent);
        filePaths.push(markdownPath);
      }

      // メタデータファイルを保存
      const metadataPath = `${sessionDir}/${this.fileNaming.metadataFilePattern}`;
      await this.gitFs.writeFile(metadataPath, JSON.stringify(metadata, null, 2));
      filePaths.push(metadataPath);

      // 自動コミット
      let commitSha: string | undefined;
      if (this.config.autoCommit) {
        const message = commitMessage || this.generateCommitMessage(session, messages.length);
        commitSha = await this.gitFs.commit(message, filePaths);
        metadata.commitSha = commitSha;
      }

      return {
        success: true,
        commitSha,
        filePaths
      };

    } catch (error) {
      console.error('Failed to save chat history:', error);
      return {
        success: false,
        filePaths: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * チャット履歴を読み込み
   */
  async loadHistory(sessionId: string, branch?: string): Promise<ChatHistoryFile | null> {
    try {
      await this.initialize();

      if (branch) {
        await this.gitFs.switchBranch(branch);
      }

      // メタデータファイルからactorIdを取得
      const sessionDirs = await this.findSessionDirectories(sessionId);
      if (sessionDirs.length === 0) {
        return null;
      }

      const sessionDir = sessionDirs[0]; // 最初の結果を使用

             // JSON形式を優先して読み込み
       const jsonPath = `${sessionDir}/${this.fileNaming.jsonFilePattern}`;
       if (await this.gitFs.exists(jsonPath)) {
         const content = await this.gitFs.readFile(jsonPath);
         return JSON.parse(content) as ChatHistoryFile;
       }

       // JSONが存在しない場合、メタデータとMarkdownから復元
       const metadataPath = `${sessionDir}/${this.fileNaming.metadataFilePattern}`;
       if (await this.gitFs.exists(metadataPath)) {
        const metadataContent = await this.gitFs.readFile(metadataPath);
        const metadata = JSON.parse(metadataContent) as ChatHistoryMetadata;
        
        // 基本的な構造を返す（メッセージはAPIから取得することを想定）
        return {
          metadata,
          session: {
            id: metadata.sessionId,
            actor_id: metadata.actorId,
            created_at: metadata.createdAt,
            updated_at: metadata.updatedAt,
            status: 'active',
            message_count: metadata.messageCount
          },
          messages: [] // 実際の実装ではAPIから取得
        };
      }

      return null;

    } catch (error) {
      console.error('Failed to load chat history:', error);
      return null;
    }
  }

  /**
   * チャット履歴を検索
   */
  async searchHistory(query: ChatHistorySearchQuery): Promise<ChatHistorySearchResult[]> {
    try {
      await this.initialize();

      const results: ChatHistorySearchResult[] = [];
      const sessionDirs = await this.getAllSessionDirectories();

      for (const sessionDir of sessionDirs) {
                 try {
           const metadataPath = `${sessionDir}/${this.fileNaming.metadataFilePattern}`;
           if (!(await this.gitFs.exists(metadataPath))) {
             continue;
           }

          const metadataContent = await this.gitFs.readFile(metadataPath);
          const metadata = JSON.parse(metadataContent) as ChatHistoryMetadata;

          // フィルタリング
          if (!this.matchesQuery(metadata, query)) {
            continue;
          }

          // 履歴ファイルを読み込み
          const historyFile = await this.loadHistoryFromDirectory(sessionDir);
          if (!historyFile) {
            continue;
          }

          // キーワード検索
          const matchedIndices = this.findMatchingMessages(historyFile.messages, query.keyword);

          results.push({
            historyFile,
            matchedMessageIndices: matchedIndices.length > 0 ? matchedIndices : undefined,
            score: this.calculateScore(historyFile, query),
            filePath: sessionDir
          });

        } catch (error) {
          console.warn(`Failed to process session directory ${sessionDir}:`, error);
        }
      }

      // スコア順でソート
      results.sort((a, b) => (b.score || 0) - (a.score || 0));

      // 件数制限
      return query.limit ? results.slice(0, query.limit) : results;

    } catch (error) {
      console.error('Failed to search chat history:', error);
      return [];
    }
  }

  /**
   * メッセージを追加保存
   */
  async appendMessages(
    sessionId: string,
    newMessages: ActorMessage[],
    commitMessage?: string
  ): Promise<ChatGitSaveResult> {
    try {
      await this.initialize();

      // 既存の履歴を読み込み
      const existingHistory = await this.loadHistory(sessionId);
      if (!existingHistory) {
        throw new Error(`Session history not found: ${sessionId}`);
      }

      // メッセージを追加
      const updatedMessages = [...existingHistory.messages, ...newMessages];
      
      // セッション情報を更新
      const updatedSession = {
        ...existingHistory.session,
        updated_at: new Date().toISOString(),
        message_count: updatedMessages.length
      };

      // 保存
      return await this.saveHistory(updatedSession, updatedMessages, commitMessage);

    } catch (error) {
      console.error('Failed to append messages:', error);
      return {
        success: false,
        filePaths: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * セッションのブランチを作成
   */
  async createSessionBranch(
    sessionId: string,
    branchName: string,
    baseBranch?: string
  ): Promise<boolean> {
    try {
      await this.initialize();

      if (baseBranch) {
        await this.gitFs.switchBranch(baseBranch);
      }

      await this.gitFs.switchBranch(branchName, true);
      return true;

    } catch (error) {
      console.error('Failed to create session branch:', error);
      return false;
    }
  }

  /**
   * 履歴のGitステータス取得
   */
  async getHistoryStatus(sessionId: string): Promise<{
    hasChanges: boolean;
    lastCommitSha?: string;
    lastCommitMessage?: string;
    branch: string;
  }> {
    try {
      await this.initialize();

      const status = await this.gitFs.getStatus();
      const commits = await this.gitFs.getCommitHistory(1);
      const branch = await this.gitFs.getCurrentBranch();

      return {
        hasChanges: status.some(s => s.status !== 'unmodified'),
        lastCommitSha: commits[0]?.oid,
        lastCommitMessage: commits[0]?.message,
        branch
      };

    } catch (error) {
      console.error('Failed to get history status:', error);
      return {
        hasChanges: false,
        branch: 'main'
      };
    }
  }

  /**
   * コミット履歴取得
   */
  async getCommitHistory(sessionId: string, limit: number = 10): Promise<{
    commitSha: string;
    message: string;
    author: string;
    date: string;
    messageCount: number;
  }[]> {
    try {
      await this.initialize();

      const commits = await this.gitFs.getCommitHistory(limit);
      
      return commits.map(commit => ({
        commitSha: commit.oid,
        message: commit.message,
        author: commit.author.name,
        date: new Date(commit.author.timestamp * 1000).toISOString(),
        messageCount: 0 // TODO: コミット時のメッセージ数を追跡
      }));

    } catch (error) {
      console.error('Failed to get commit history:', error);
      return [];
    }
  }

  // プライベートメソッド

  /**
   * セッションディレクトリのパスを取得
   */
  private getSessionDirectory(actorId: string, sessionId: string): string {
    const baseDir = this.config.baseDirectory || '';
    const pattern = this.fileNaming.sessionDirPattern
      .replace('{actorId}', actorId)
      .replace('{sessionId}', sessionId);
    
    return baseDir ? `${baseDir}/${pattern}` : pattern;
  }

  /**
   * Markdownに変換
   */
  private convertToMarkdown(historyFile: ChatHistoryFile): string {
    const { metadata, session, messages } = historyFile;
    let markdown = '';

    // タイトル
    const title = this.markdownOptions.titleFormat
      .replace('{sessionId}', session.id)
      .replace('{actorId}', session.actor_id);
    markdown += `${title}\n\n`;

    // メタデータ
    if (this.markdownOptions.includeMetadata) {
      markdown += '## Metadata\n\n';
      markdown += `- **Session ID**: ${metadata.sessionId}\n`;
      markdown += `- **Actor ID**: ${metadata.actorId}\n`;
      markdown += `- **Created**: ${metadata.createdAt}\n`;
      markdown += `- **Updated**: ${metadata.updatedAt}\n`;
      markdown += `- **Message Count**: ${metadata.messageCount}\n`;
      markdown += `- **Branch**: ${metadata.branch}\n\n`;
    }

    // 目次
    if (this.markdownOptions.includeToc) {
      markdown += '## Table of Contents\n\n';
      messages.forEach((msg, index) => {
        const timestamp = new Date(msg.created_at).toLocaleString();
        markdown += `${index + 1}. [${msg.role} (${timestamp})](#message-${index + 1})\n`;
      });
      markdown += '\n';
    }

    // メッセージ
    markdown += '## Messages\n\n';
    messages.forEach((msg, index) => {
      const timestamp = new Date(msg.created_at).toLocaleString();
      const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
      
      markdown += `### Message ${index + 1}\n\n`;
      markdown += `**Role**: ${msg.role}  \n`;
      markdown += `**Timestamp**: ${timestamp}  \n`;
      markdown += `**ID**: ${msg.id}\n\n`;
      markdown += `${content}\n\n`;
      markdown += '---\n\n';
    });

    return markdown;
  }

  /**
   * コミットメッセージを生成
   */
  private generateCommitMessage(session: ActorSession, messageCount: number): string {
    const template = this.config.commitMessageTemplate || 
      'チャット履歴を更新: セッション {sessionId} ({messageCount} メッセージ)';
    
    return template
      .replace('{sessionId}', session.id)
      .replace('{actorId}', session.actor_id)
      .replace('{messageCount}', messageCount.toString());
  }

     /**
    * セッションディレクトリを検索
    */
   private async findSessionDirectories(sessionId: string): Promise<string[]> {
     // 簡易実装：実際にはGitファイルシステムで検索
     const baseDir = this.config.baseDirectory || 'sessions';
     const searchPattern = new RegExp(`.*/${sessionId}$`);
     
     try {
       const files = await this.gitFs.readDir(baseDir);
       const result: string[] = [];
       
       for (const file of files) {
         const fullPath = `${baseDir}/${file}`;
         if (file === sessionId || file.endsWith(`/${sessionId}`)) {
           result.push(fullPath);
         }
       }
       
       return result;
     } catch {
       return [];
     }
   }

     /**
    * 全セッションディレクトリを取得
    */
   private async getAllSessionDirectories(): Promise<string[]> {
     const baseDir = this.config.baseDirectory || 'sessions';
     
     try {
       const files = await this.gitFs.readDir(baseDir);
       return files.map(file => `${baseDir}/${file}`);
     } catch {
       return [];
     }
   }

     /**
    * ディレクトリから履歴ファイルを読み込み
    */
   private async loadHistoryFromDirectory(sessionDir: string): Promise<ChatHistoryFile | null> {
     try {
       const jsonPath = `${sessionDir}/${this.fileNaming.jsonFilePattern}`;
       if (await this.gitFs.exists(jsonPath)) {
         const content = await this.gitFs.readFile(jsonPath);
         return JSON.parse(content) as ChatHistoryFile;
       }
       return null;
     } catch {
       return null;
     }
   }

  /**
   * クエリにマッチするかチェック
   */
  private matchesQuery(metadata: ChatHistoryMetadata, query: ChatHistorySearchQuery): boolean {
    if (query.sessionId && metadata.sessionId !== query.sessionId) return false;
    if (query.actorId && metadata.actorId !== query.actorId) return false;
    if (query.branch && metadata.branch !== query.branch) return false;
    
    if (query.dateFrom || query.dateTo) {
      const createdAt = new Date(metadata.createdAt);
      if (query.dateFrom && createdAt < new Date(query.dateFrom)) return false;
      if (query.dateTo && createdAt > new Date(query.dateTo)) return false;
    }
    
    return true;
  }

  /**
   * キーワードにマッチするメッセージを検索
   */
  private findMatchingMessages(messages: ActorMessage[], keyword?: string): number[] {
    if (!keyword) return [];
    
    const lowerKeyword = keyword.toLowerCase();
    return messages
      .map((msg, index) => {
        const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
        return content.toLowerCase().includes(lowerKeyword) ? index : -1;
      })
      .filter(index => index !== -1);
  }

  /**
   * 検索スコアを計算
   */
  private calculateScore(historyFile: ChatHistoryFile, query: ChatHistorySearchQuery): number {
    let score = 0;
    
    // 完全一致ボーナス
    if (query.sessionId === historyFile.metadata.sessionId) score += 100;
    if (query.actorId === historyFile.metadata.actorId) score += 50;
    
    // キーワードマッチ
    if (query.keyword) {
      const matches = this.findMatchingMessages(historyFile.messages, query.keyword);
      score += matches.length * 10;
    }
    
    // 新しいセッションにボーナス
    const ageInDays = (Date.now() - new Date(historyFile.metadata.createdAt).getTime()) / (1000 * 60 * 60 * 24);
    score += Math.max(0, 10 - ageInDays);
    
    return score;
  }
} 